import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  MAX_GROUP_DEPTH,
  addGroupTo,
  addLeafTo,
  removeNode,
  updateGroup,
  updateLeaf,
} from '../../lib/conditionTree';
import type { ConditionCombinator, ConditionTreeGroup } from '../../lib/conditionTree';
import type { ToastSeverity } from '../../lib/useToast';
import type { ConditionLeafType } from '../../model/types';
import { ConditionLeafRow } from './ConditionLeafRow';

const LEAF_TYPE_GROUPS: { headingKey: string; types: ConditionLeafType[] }[] = [
  {
    headingKey: 'conditionEditor.group.cart',
    types: ['MIN_SUBTOTAL', 'MIN_ITEM_QUANTITY', 'PRODUCT_IN_SET', 'CATEGORY_IN_SET'],
  },
  {
    headingKey: 'conditionEditor.group.customer',
    types: ['CUSTOMER_IN_GROUP', 'CUSTOMER_KIND', 'CUSTOMER_BIRTHDAY', 'ORDER_COUNT_AT_LEAST'],
  },
  {
    headingKey: 'conditionEditor.group.context',
    types: [
      'CHANNEL_IN',
      'STORE_IN',
      'DELIVERY_ZONE_IN',
      'SCHEDULE',
      'PAYMENT_METHOD_IN',
      'DELIVERY_METHOD_IN',
      'COUPON_PRESENT',
    ],
  },
];

export interface ConditionGroupEditorProps {
  root: ConditionTreeGroup;
  group: ConditionTreeGroup;
  depth: number;
  disabled?: boolean;
  onRootChange: (updater: (root: ConditionTreeGroup) => ConditionTreeGroup) => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** Recursive AND/OR/NOT group editor (plan-011 §8.3-C) — see `lib/conditionTree.ts` for the model. */
export function ConditionGroupEditor({
  root,
  group,
  depth,
  disabled = false,
  onRootChange,
  onResult,
}: ConditionGroupEditorProps) {
  const { t } = useTranslation('promotions');
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const isRoot = depth === 0;

  const conditionCount = group.children.length;

  return (
    <Box
      role="group"
      aria-label={t('conditionEditor.groupAriaLabel', {
        combinator: t(`conditionEditor.combinator.${group.combinator}`),
        count: conditionCount,
      })}
      sx={{
        border: isRoot ? '1px dashed' : 'none',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        ...(isRoot
          ? {}
          : {
              pl: 3,
              borderLeft: '2px solid',
              borderLeftColor: 'primary.light',
              ml: 0,
            }),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          disabled={disabled}
          value={group.combinator}
          onChange={(_e, next: ConditionCombinator | null) => {
            if (!next) return;
            onRootChange((r) => updateGroup(r, group.uiId, (g) => ({ ...g, combinator: next })));
          }}
        >
          <ToggleButton value="AND">{t('conditionEditor.combinatorToggle.AND')}</ToggleButton>
          <ToggleButton value="OR">{t('conditionEditor.combinatorToggle.OR')}</ToggleButton>
        </ToggleButtonGroup>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={group.not}
              disabled={disabled}
              onChange={(e) =>
                onRootChange((r) => updateGroup(r, group.uiId, (g) => ({ ...g, not: e.target.checked })))
              }
            />
          }
          label={t('conditionEditor.notLabel')}
        />

        <Box sx={{ flex: 1 }} />

        {!isRoot ? (
          <Tooltip title={t('conditionEditor.removeGroupAria')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('conditionEditor.removeGroupAria')}
                disabled={disabled}
                onClick={() => onRootChange((r) => removeNode(r, group.uiId))}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      {conditionCount === 0 && isRoot ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('conditionEditor.emptyHint')}
        </Typography>
      ) : null}

      <Stack spacing={1.5}>
        {group.children.map((child) =>
          child.kind === 'leaf' ? (
            <ConditionLeafRow
              key={child.uiId}
              leaf={child}
              disabled={disabled}
              onResult={onResult}
              onChange={(updated) =>
                onRootChange((r) => updateLeaf(r, child.uiId, () => updated))
              }
              onRemove={() => onRootChange((r) => removeNode(r, child.uiId))}
            />
          ) : (
            <ConditionGroupEditor
              key={child.uiId}
              root={root}
              group={child}
              depth={depth + 1}
              disabled={disabled}
              onRootChange={onRootChange}
              onResult={onResult}
            />
          ),
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          disabled={disabled}
          onClick={(e) => setAddMenuAnchor(e.currentTarget)}
        >
          {t('conditionEditor.addCondition')}
        </Button>
        <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}>
          {LEAF_TYPE_GROUPS.flatMap((leafGroup) => [
            <ListSubheader key={leafGroup.headingKey}>{t(leafGroup.headingKey)}</ListSubheader>,
            ...leafGroup.types.map((type) => (
              <MenuItem
                key={type}
                onClick={() => {
                  onRootChange((r) => addLeafTo(r, group.uiId, type));
                  setAddMenuAnchor(null);
                }}
              >
                {t(`conditionEditor.leafType.${type}`)}
              </MenuItem>
            )),
          ])}
        </Menu>

        {depth < MAX_GROUP_DEPTH ? (
          <Button
            size="small"
            startIcon={<CreateNewFolderRoundedIcon />}
            disabled={disabled}
            onClick={() => onRootChange((r) => addGroupTo(r, group.uiId))}
          >
            {t('conditionEditor.addGroup')}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}
